import SAreaChart from "components/shareable/DATA_PRESENTATION/Charts/SAreaChart";
import DebugBox from "components/shareable/DebugBox";
import CardSymphony from "elements/CardSymphony";
import Stat from "elements/Stat";
import useWebsiteBuilderAnalytics from "Hooks/useWebsiteBuilderAnalytics";
import { CSSProperties, useContext, useEffect, useState } from "react";
import Colors from 'modules/Colors';
import dayjs from "dayjs";
import SwitchSymphony from "elements/SwitchSymphony";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import TabSymphony from "elements/TabSymphony";
import SBarChart from "components/shareable/DATA_PRESENTATION/Charts/SBarChart";
import millify from "millify";
import Sty from "modules/Sty";
import Icon from "elements/Icon";
import useWebsiteBuilder, {
    CollectDataInputType,
    WebsiteBuilderContent,
    WebsiteBuilderContentType
} from 'Hooks/useWebsiteBuilder';
import { PlatformItem } from "elements/PlatformLink";
import TableElement, { ColDef } from "elements/TableElement";
import WebsiteBuilderEmptyState from "./WebsiteBuilderEmptyState";
import { formatPhoneNumber, generateAndDownloadCsv } from "modules/Utils";
import Loader from "elements/Loader";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import DatePickerSymphony from "elements/DatePickerSymphony";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import useCurrentBrand from "Hooks/useCurrentBrand";
import BlurredGraph from "elements/BlurredGraph";
import ButtonSymphony from "elements/ButtonSymphony";

enum WebsiteChartTypes {
    COUNTRY = 'country',
    CITY = 'city',
    EXTERNAL_LINKS = 'externalLinks',
    MUSIC = 'music',
    SOCIAL_PLATFORMS = 'socialPlatforms',
    STREAMING_PLATFORMS = 'streamingPlatforms',
    TOUR = 'tour',
    VIDEOS = 'videos',
}

/** Date range options for filtering analytics data */
enum DateRangeOptions {
    LAST_7_DAYS = 'last_7_days',
    LAST_30_DAYS = 'last_30_days',
    LAST_3_MONTHS = 'last_3_months',
    ALL_TIME = 'all_time',
    CUSTOM = 'custom',
}

const WebsiteBuilderAnalytics = () => {
    const { handleOpenModal: handleOpenPaywallModal } = useContext(UpgradeModalContext);
    const { isPaidBrand } = useCurrentBrand();

    const {
        websiteContent,
        websiteBuilderData
    } = useWebsiteBuilder();

    const {
        websiteAnalyticsData,
        getWebsiteAnalytics,

        websiteCollectedData,
        getWebsiteCollectedData,
        isLoadingWebsiteCollectedData,

        websiteTourData,
        getWebsiteTourData,
        isLoadingWebsiteTourData,
    } = useWebsiteBuilderAnalytics();

    const websiteId = websiteBuilderData?.id;

    const isCollectDataContentExists = websiteContent?.some((content) => content.type === WebsiteBuilderContentType.COLLECT_DATA);
    const isTourContentExists = websiteContent?.some((content) => content.type === WebsiteBuilderContentType.TOUR);

    // Date range states
    /** Currently selected date range option */
    const [dateRange, setDateRange] = useState<DateRangeOptions>(DateRangeOptions.LAST_7_DAYS);
    /** Start date for analytics data filtering */
    const [startDate, setStartDate] = useState<Date | null>(dayjs().subtract(7, 'day').toDate());
    /** End date for analytics data filtering */
    const [endDate, setEndDate] = useState<Date | null>(dayjs().toDate());
    /** Controls visibility of custom date range picker section */
    const [showCustomDateRange, setShowCustomDateRange] = useState<boolean>(false);

    /** Available date range options for the dropdown */
    const DATE_RANGE_OPTIONS: SelectOptionItem[] = [
        {
            value: DateRangeOptions.LAST_7_DAYS,
            title: 'Last 7 Days'
        },
        {
            value: DateRangeOptions.LAST_30_DAYS,
            title: 'Last 30 Days',
            icon: 'gem',
        },
        {
            value: DateRangeOptions.LAST_3_MONTHS,
            title: 'Last 3 Months',
            icon: 'gem',
        },
        {
            value: DateRangeOptions.ALL_TIME,
            title: 'All Time',
            icon: 'gem',
        },
        {
            value: DateRangeOptions.CUSTOM,
            title: 'Custom',
            icon: 'gem',
        },
    ];

    /** 
     * Handles changes to the date range dropdown selection
     * Sets appropriate start/end dates based on selection
     */
    const handleDateRangeChange = async (value: string) => {
        if (!websiteId) {
            return;
        }

        const selectedOption = value as DateRangeOptions;

        if (!isPaidBrand && selectedOption !== DateRangeOptions.LAST_7_DAYS) {
            handleOpenPaywallModal();
            return;
        }

        setDateRange(selectedOption);

        let newStartDate: Date | null = null;
        let newEndDate: Date | null = dayjs().toDate();
        let showCustom = false;

        // Set date range based on selected option
        switch (selectedOption) {
            case DateRangeOptions.LAST_7_DAYS:
                newStartDate = dayjs().subtract(7, 'day').toDate();
                break;
            case DateRangeOptions.LAST_30_DAYS:
                newStartDate = dayjs().subtract(30, 'day').toDate();
                break;
            case DateRangeOptions.LAST_3_MONTHS:
                newStartDate = dayjs().subtract(3, 'month').toDate();
                break;
            case DateRangeOptions.ALL_TIME:
                newStartDate = null;
                break;
            case DateRangeOptions.CUSTOM:
                showCustom = true;
                newStartDate = startDate || dayjs().subtract(7, 'day').toDate();
                newEndDate = endDate || dayjs().toDate();
                break;
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setShowCustomDateRange(showCustom);

        await getWebsiteAnalytics({
            websiteId,
            startDate: newStartDate ? dayjs(newStartDate).format('YYYY-MM-DD') : '',
            endDate: dayjs(newEndDate).format('YYYY-MM-DD'),
            mock: false,
        });
    };

    /** Handles changes to the start date in custom date range */
    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);

        // If start date is after end date, update end date to be 7 days after start date
        if (date && endDate && dayjs(date).isAfter(endDate)) {
            setEndDate(dayjs(date).add(7, 'day').toDate());
        }
    };

    /** Handles changes to the end date in custom date range */
    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);

        // If end date is before start date, update start date to be 7 days before end date
        if (date && startDate && dayjs(date).isBefore(startDate)) {
            setStartDate(dayjs(date).subtract(7, 'day').toDate());
        }
    };

    const currentCollectDataContent: WebsiteBuilderContent | undefined = websiteContent?.find((content) => content.type === WebsiteBuilderContentType.COLLECT_DATA);
    const currentContentId = currentCollectDataContent?.id || '';

    const columnDefsCollectData: ColDef[] = [
        {
            title: 'Email',
            field: 'email',
            flex: 2,
            renderCell: (row: any) => {
                return <div>
                    {row.email ? row.email : '–'}
                </div>
            }
        },
        {
            title: 'Phone',
            field: 'phone',
            flex: 2,
            renderCell: (row: any) => {
                return <div>
                    {row.phone ? formatPhoneNumber(row.phone) : '–'}
                </div>
            }
        },
        ...(currentCollectDataContent?.collectDataContent?.info ?
            currentCollectDataContent?.collectDataContent?.info
                .filter((infoItem: any) => infoItem.type !== CollectDataInputType.EMAIL_PHONE)
                .map((infoItem: any) => ({
                    title: infoItem.title,
                    field: infoItem.title,
                })) || []
            :
            [])
    ]

    const columnDefsTourData: ColDef[] = [
        {
            title: 'Email',
            field: 'email',
            flex: 3,
            minWidth: 200,
            renderCell: (row: any) => {
                return <div>
                    {row.email ? row.email : '–'}
                </div>
            }
        },
        {
            title: 'Phone',
            field: 'phone',
            flex: 2,
            minWidth: 150,
            renderCell: (row: any) => {
                return <div>
                    {row.phone ? formatPhoneNumber(row.phone) : '–'}
                </div>
            }
        },
        {
            title: 'Name',
            field: 'name',
            flex: 2,
            minWidth: 150,
            renderCell: (row: any) => {
                return <div>
                    {row.name || '–'}
                </div>
            }
        },
        {
            title: 'City',
            field: 'city',
            flex: 2,
            minWidth: 150,
            renderCell: (row: any) => {
                return <div>
                    {row.city || '–'}
                </div>
            }
        },
        {
            title: 'Region',
            field: 'region',
            flex: 2,
            renderCell: (row: any) => {
                return <div>
                    {row.region || '–'}
                </div>
            }
        },
        {
            title: 'Country',
            field: 'country',
            flex: 2,
            renderCell: (row: any) => {
                return <div>
                    {row.country || '–'}
                </div>
            }
        },
        {
            title: 'Date',
            field: 'date',
            flex: 2,
            minWidth: 150,
            renderCell: (row: any) => {
                return <div>
                    {row.date ? dayjs(row.date).format('MMM D, YYYY') : '–'}
                </div>
            }
        },
        {
            title: 'Venue',
            field: 'venue',
            flex: 2,
            minWidth: 150,
            renderCell: (row: any) => {
                return <div>
                    {row.venue || '–'}
                </div>
            }
        },
    ]

    const WEBSITE_ANALYTICS_TABS: TabItem[] = [
        {
            id: 'content',
            title: 'Content Analytics',
        },
        {
            id: 'demographics',
            title: 'Demographics',
        },
        ...(isCollectDataContentExists ? [{
            id: 'signupData',
            title: 'Collected Data',
        }] : []),
        ...(isTourContentExists ? [{
            id: 'tourData',
            title: 'Tour Data',
        }] : []),
    ]

    const [selectedWebsiteAnalyticsTab, setSelectedWebsiteAnalyticsTab] = useState<TabItem>(WEBSITE_ANALYTICS_TABS[0]);

    const [isPageViewsGraph, setIsPageViewsGraph] = useState<boolean>(true);
    const [isTourLinkClicksGraph, setIsTourLinkClicksGraph] = useState<boolean>(true);
    const [isStreamingLinkClicksGraph, setIsStreamingLinkClicksGraph] = useState<boolean>(true);
    const [isSocialLinkClicksGraph, setIsSocialLinkClicksGraph] = useState<boolean>(true);
    const [isExternalLinkClicksGraph, setIsExternalLinkClicksGraph] = useState<boolean>(true);
    const [isSignupsGraph, setIsSignupsGraph] = useState<boolean>(true);

    useEffect(() => {
        if (selectedWebsiteAnalyticsTab.id === 'signupData' && websiteId) {
            if (isCollectDataContentExists && !isLoadingWebsiteCollectedData) {
                getWebsiteCollectedData({
                    websiteId,
                });
            }
        }

        if (selectedWebsiteAnalyticsTab.id === 'tourData' && websiteId) {
            if (isTourContentExists && !isLoadingWebsiteTourData) {
                getWebsiteTourData({
                    websiteId,
                });
            }
        }
    }, [selectedWebsiteAnalyticsTab]);

    if (!websiteAnalyticsData) {
        return (
            <Loader
                className="websiteAnalyticsLoader"
                title="Loading analytics..."
                isCentered
            />
        )
    }

    const {
        topLevelStats,
        geographicBreakdown,
        contentAnalytics
    } = websiteAnalyticsData;

    const {
        pageViews,
        tourLinkClicks,
        streamingLinkClicks,
        socialLinkClicks,
        externalLinkClicks,
        signups,
    } = topLevelStats

    const {
        externalLinks,
        music,
        socialPlatforms,
        streamingPlatforms,
        tour,
        videos,
    } = contentAnalytics;

    const {
        countryBreakdown,
        cityBreakdown,
    } = geographicBreakdown;

    const calculateTotal = (items?: any[]) => {
        let total = 0;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                total += (items[i].value || 0);
            }
        }
        return total;
    };

    const pageViewsCount = calculateTotal(pageViews);
    const tourLinkClicksCount = calculateTotal(tourLinkClicks);
    const streamingLinkClicksCount = calculateTotal(streamingLinkClicks);
    const socialLinkClicksCount = calculateTotal(socialLinkClicks);
    const externalLinkClicksCount = calculateTotal(externalLinkClicks);
    const signupsCount = calculateTotal(signups);

    const defaultBarChartProps = {
        direction: 'horizontal' as 'horizontal',
        yAxisKey: 'uniqueId',
        xAxisKey: 'count',
        tickHeight: 32,
        tickWidth: 200,
        barHeight: 24,
        barWidth: 150,
    }

    const defaultCardGraphProps = {
        style: sty.graphCard,
        isPadding16: true,
        size: 'small' as 'small',
    }

    const getIconName = (iconName: string) => {
        switch (iconName) {
            case 'apple_music':
                return 'appleMusic';
            case 'amazon_music':
                return 'amazonMusic';
            default:
                return iconName;
        }
    }

    const getData = () => {
        return pageViews.map((item: any, index: number) => {
            let data: any = {
                date: dayjs(item.date).format('YYYY/MM/DD'),
            }

            if (pageViews?.length > 0 && isPageViewsGraph) {
                data.pageViews = pageViews[index]?.value || 0;
            }

            if (tourLinkClicks?.length > 0 && isTourLinkClicksGraph) {
                data.tourLinkClicks = tourLinkClicks[index]?.value || 0;
            }

            if (streamingLinkClicks?.length > 0 && isStreamingLinkClicksGraph) {
                data.streamingLinkClicks = streamingLinkClicks[index]?.value || 0;
            }

            if (socialLinkClicks?.length > 0 && isSocialLinkClicksGraph) {
                data.socialLinkClicks = socialLinkClicks[index]?.value || 0;
            }

            if (externalLinks?.length > 0 && isExternalLinkClicksGraph) {
                data.externalLinkClicks = externalLinkClicks[index]?.value || 0;
            }

            if (signups?.length > 0 && isSignupsGraph) {
                data.signups = signups[index]?.value || 0;
            }

            return data;
        })
    }

    const createDataSets = () => {
        let dataSets: any[] = [];

        if (pageViews?.length > 0) {
            dataSets.push({ key: 'pageViews', title: 'Page Views', color: '#00A3FF' });
        }
        if (tourLinkClicks?.length > 0) {
            dataSets.push({ key: 'tourLinkClicks', title: 'Tour Link Clicks', color: '#4CAF50' });
        }
        if (streamingLinkClicks?.length > 0) {
            dataSets.push({ key: 'streamingLinkClicks', title: 'Streaming Link Clicks', color: '#FFC107' });
        }
        if (socialLinkClicks?.length > 0) {
            dataSets.push({ key: 'socialLinkClicks', title: 'Social Link Clicks', color: Colors.purple });
        }
        if (externalLinkClicks?.length > 0) {
            dataSets.push({ key: 'externalLinkClicks', title: 'External Link Clicks', color: '#FF5722' });
        }
        if (signups?.length > 0) {
            dataSets.push({ key: 'signups', title: 'Signups', color: '#FF7700' });
        }

        return dataSets;
    }

    const handleDownloadCollectDataCsv = () => {
        if (!isPaidBrand) {
            handleOpenPaywallModal();
            return;
        }

        if (!currentContentId || !websiteCollectedData || !websiteCollectedData[currentContentId]?.length) {
            return;
        }

        generateAndDownloadCsv({ rows: websiteCollectedData[currentContentId], cols: columnDefsCollectData });
    }

    const handleDownloadTourDataCsv = () => {
        if (!isPaidBrand) {
            handleOpenPaywallModal();
            return;
        }

        if (!websiteTourData || !websiteTourData?.length) {
            return;
        }

        generateAndDownloadCsv({ rows: websiteTourData, cols: columnDefsTourData });
    }

    const renderBarChartTick = (payload: { value: string }, chartType: WebsiteChartTypes, count: number, item?: any) => {
        let content: any = payload.value;

        switch (chartType) {
            case WebsiteChartTypes.SOCIAL_PLATFORMS:
            case WebsiteChartTypes.STREAMING_PLATFORMS:
                const iconName = getIconName(payload.value);
                content = <Icon
                    className="socialIcon"
                    icon={iconName}
                />
                break;
            case WebsiteChartTypes.EXTERNAL_LINKS:
            case WebsiteChartTypes.MUSIC:
            case WebsiteChartTypes.TOUR:
            case WebsiteChartTypes.VIDEOS:
                const link = payload.value;
                content = <div
                    className="linkContainer"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '75%',
                    }}
                >
                    <Icon
                        className="socialIcon"
                        style={{ marginRight: 4 }}
                        icon="externalLink"
                        onClick={() => window.open(link, '_blank')}
                    />
                    <div
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            paddingRight: 16,
                        }}
                    >
                        {item?.tourCity}
                        {item?.externalLinksContent?.platforms.find((platform: PlatformItem) => platform.link === link)?.title}
                        {item?.videoContent?.thumbnail &&
                            <img
                                src={item.videoContent.thumbnail}
                                style={{
                                    width: 24,
                                    height: 24,
                                }}
                            />
                        }
                        {item?.musicContent?.selectedMusic?.title &&
                            <div>
                                {item.musicContent.selectedMusic?.thumbnail_url}
                            </div>
                        }
                    </div>
                </div>
                break;
        }

        return (
            <div
                className="tickContainer"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingTop: 3,
                }}
            >
                {content}
                <div>
                    {millify(count)}
                </div>
            </div>
        )
    }


    const renderTopLevelStats = () => {
        return (
            <div className="topLevelStatsContainer" style={sty.topLevelStatsContainer}>
                <Stat
                    className="websiteAnalyticsStat"
                    style={sty.statStyle}
                    icon="click"
                    stats={[
                        {
                            title: 'Page Views',
                            value: pageViewsCount,
                            displayType: 'number'
                        },
                    ]}
                />
                {tourLinkClicks?.length > 0 &&
                    <Stat
                        className="websiteAnalyticsStat"
                        style={sty.statStyle}
                        icon="click"
                        stats={[
                            {
                                title: 'Tour Link Clicks',
                                value: tourLinkClicksCount,
                                displayType: 'number'
                            },
                        ]}
                    />
                }
                {streamingLinkClicks?.length > 0 &&
                    <Stat
                        className="websiteAnalyticsStat"
                        style={sty.statStyle}
                        icon="click"
                        stats={[
                            {
                                title: 'Streaming Link Clicks',
                                value: streamingLinkClicksCount,
                                displayType: 'number'
                            },
                        ]}
                    />
                }
                {socialLinkClicks?.length > 0 &&
                    <Stat
                        className="websiteAnalyticsStat"
                        style={sty.statStyle}
                        icon="click"
                        stats={[
                            {
                                title: 'Social Link Clicks',
                                value: socialLinkClicksCount,
                                displayType: 'number'
                            },
                        ]}
                    />
                }
                {externalLinkClicks?.length > 0 &&
                    <Stat
                        className="websiteAnalyticsStat"
                        style={sty.statStyle}
                        icon="click"
                        stats={[
                            {
                                title: 'External Link Clicks',
                                value: externalLinkClicksCount,
                                displayType: 'number'
                            },
                        ]}
                    />
                }
                {signups?.length > 0 &&
                    <Stat
                        className="websiteAnalyticsStat"
                        style={sty.statStyle}
                        icon="click"
                        stats={[
                            {
                                title: 'Signups',
                                value: signupsCount,
                                displayType: 'number'
                            },
                        ]}
                    />
                }
            </div>
        )
    }

    const renderTopLevelStatsGraph = () => {
        return (
            <div className="chartContainer" style={sty.chartContainer}>
                <SAreaChart
                    className="topLevelStatsGraph"
                    xAxisKey="date"
                    data={getData()}
                    dataSets={createDataSets()}
                    width={500}
                    height={300}
                    showLegend
                />
            </div>
        )
    }

    const renderTopLevelStatsToggle = () => {
        return (
            <div
                className="topLevelStatsToggleContainer"
                style={sty.topLevelStatsToggleContainer}
            >
                {pageViews?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: '#00A3FF',
                        }}
                        type="checkbox"
                        checked={isPageViewsGraph}
                        label="Page Views"
                        onChange={(checked) => setIsPageViewsGraph(checked)}
                    />
                }
                {tourLinkClicks?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: '#4CAF50',
                        }}
                        type="checkbox"
                        checked={isTourLinkClicksGraph}
                        label="Tour Link Clicks"
                        onChange={(checked) => setIsTourLinkClicksGraph(checked)}
                    />
                }
                {streamingLinkClicks?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: '#FFC107',
                        }}
                        type="checkbox"
                        checked={isStreamingLinkClicksGraph}
                        label="Streaming Link Clicks"
                        onChange={(checked) => setIsStreamingLinkClicksGraph(checked)}
                    />
                }
                {socialLinkClicks?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: Colors.purple,
                        }}
                        type="checkbox"
                        checked={isSocialLinkClicksGraph}
                        label="Social Link Clicks"
                        onChange={(checked) => setIsSocialLinkClicksGraph(checked)}
                    />
                }
                {externalLinkClicks?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: '#FF5722',
                        }}
                        type="checkbox"
                        checked={isExternalLinkClicksGraph}
                        label="External Link Clicks"
                        onChange={(checked) => setIsExternalLinkClicksGraph(checked)}
                    />
                }
                {signups?.length > 0 &&
                    <SwitchSymphony
                        className="topLevelStatsToggle"
                        style={{
                            color: '#FF7700',
                        }}
                        type="checkbox"
                        checked={isSignupsGraph}
                        label="Signups"
                        onChange={(checked) => setIsSignupsGraph(checked)}
                    />
                }
            </div>
        )
    }

    const renderDemographicsTab = () => {
        return (
            <TabSymphony
                className="demographicsTab"
                style={sty.graphTab}
                visible={selectedWebsiteAnalyticsTab.id === 'demographics'}
            >
                {isPaidBrand ?
                    <>
                        <CardSymphony
                            className="demographicsCard"
                            title="Country Breakdown"
                            subtitle="See how your website is performing by country"
                            {...defaultCardGraphProps}
                        >
                            <SBarChart
                                {...defaultBarChartProps}
                                data={countryBreakdown
                                    .sort((a: any, b: any) => b.count - a.count)
                                    .map((countryEntry: any) => ({
                                        uniqueId: countryEntry.country,
                                        count: countryEntry.count,
                                    }))}
                                tick={(payload) => {
                                    const item = countryBreakdown.find((item: { country: string, count: number }) => item.country === payload.value) || { country: '', count: 0 };
                                    return renderBarChartTick(payload, WebsiteChartTypes.COUNTRY, item.count);
                                }}
                            />
                        </CardSymphony>

                        <CardSymphony
                            className="demographicsCard"
                            title="City Breakdown"
                            subtitle="See how your website is performing by city"
                            {...defaultCardGraphProps}
                        >
                            <SBarChart
                                {...defaultBarChartProps}
                                data={cityBreakdown
                                    .sort((a: any, b: any) => b.count - a.count)
                                    .map((cityEntry: any) => ({
                                        uniqueId: cityEntry.city,
                                        count: cityEntry.count,
                                    }))}
                                tick={(payload) => {
                                    const item = cityBreakdown.find((item: { city: string, count: number }) => item.city === payload.value) || { city: '', count: 0 };
                                    return renderBarChartTick(payload, WebsiteChartTypes.CITY, item.count);
                                }}
                            />
                        </CardSymphony>
                    </>
                    :
                    <>
                        <BlurredGraph
                            className="demographicsBlurredGraph"
                            graph="topDemographics"
                            buttonText="Upgrade to Lite"
                            upgradeSource="Website Builder - Demographics Analytics"
                        />
                    </>
                }
            </TabSymphony>
        )
    }

    const renderContentAnalyticsTab = () => {
        const isEmpty = !externalLinks?.length && !music?.length && !socialPlatforms?.length && !streamingPlatforms?.length && !tour?.length && !videos?.length;

        return (
            <TabSymphony
                className="contentAnalyticsTab"
                style={sty.graphTab}
                visible={selectedWebsiteAnalyticsTab.id === 'content'}
            >
                {isPaidBrand ?
                    <>
                        {isEmpty &&
                            <WebsiteBuilderEmptyState
                                className="contentAnalyticsEmptyState"
                                title="No content analytics data"
                                subtitle="Add content to your website to see the analytics"
                            />
                        }
                        {externalLinks?.length > 0 && (
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="External Links"
                                subtitle="See how your website is performing by external links"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={externalLinks
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((link: any) => ({
                                            uniqueId: link.linkUrl,
                                            count: link.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = externalLinks.find((item: { linkUrl: string, value: number }) => item.linkUrl === payload.value) || { linkUrl: '', value: 0 };
                                        const content = websiteContent?.find((content: WebsiteBuilderContent) => content.externalLinksContent?.platforms.find((platform: PlatformItem) => platform.link === item.linkUrl));
                                        return renderBarChartTick(payload, WebsiteChartTypes.EXTERNAL_LINKS, item.value, content);
                                    }}
                                />
                            </CardSymphony>
                        )}

                        {music?.length > 0 && (
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="Music"
                                subtitle="See how your website is performing by music"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={music
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((musicItem: any) => ({
                                            uniqueId: musicItem.spotifyUrl,
                                            count: musicItem.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = music.find((item: { spotifyUrl: string, value: number }) => item.spotifyUrl === payload.value) || { spotifyUrl: '', value: 0 };
                                        const content = websiteContent?.find((content: WebsiteBuilderContent) => content.musicContent?.selectedMusic?.spotify_uri === item.spotifyUrl);
                                        return renderBarChartTick(payload, WebsiteChartTypes.MUSIC, item.value, content);
                                    }}
                                />
                            </CardSymphony>
                        )}

                        {socialPlatforms?.length > 0 &&
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="Social Platforms"
                                subtitle="See how your website is performing by social platforms"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={socialPlatforms
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((socialPlatform: any) => ({
                                            uniqueId: socialPlatform.platform,
                                            count: socialPlatform.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = socialPlatforms.find((item: { platform: string, value: number }) => item.platform === payload.value) || { platform: '', value: 0 };
                                        return renderBarChartTick(payload, WebsiteChartTypes.SOCIAL_PLATFORMS, item.value);
                                    }}
                                />
                            </CardSymphony>
                        }

                        {streamingPlatforms?.length > 0 && (
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="Streaming Platforms"
                                subtitle="See how your website is performing by streaming platforms"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={streamingPlatforms
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((streamingPlatform: any) => ({
                                            uniqueId: streamingPlatform.platform,
                                            count: streamingPlatform.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = streamingPlatforms.find((item: { platform: string, value: number }) => item.platform === payload.value) || { platform: '', value: 0 };
                                        return renderBarChartTick(payload, WebsiteChartTypes.STREAMING_PLATFORMS, item.value);
                                    }}
                                />
                            </CardSymphony>
                        )}

                        {tour?.length > 0 && (
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="Tour"
                                subtitle="See how your website is performing by tour"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={tour
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((tourItem: any) => ({
                                            uniqueId: tourItem.ticketUrl,
                                            count: tourItem.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = tour.find((item: { ticketUrl: string, value: number }) => item.ticketUrl === payload.value) || { ticketUrl: '', value: 0 };
                                        return renderBarChartTick(payload, WebsiteChartTypes.TOUR, item.value, item);
                                    }}
                                />
                            </CardSymphony>
                        )}

                        {videos?.length > 0 && (
                            <CardSymphony
                                className="contentAnalyticsCard"
                                title="Videos"
                                subtitle="See how your website is performing by videos"
                                {...defaultCardGraphProps}
                            >
                                <SBarChart
                                    {...defaultBarChartProps}
                                    data={videos
                                        .sort((a: any, b: any) => b.value - a.value)
                                        .map((video: any) => ({
                                            uniqueId: video.videoUrl,
                                            count: video.value,
                                        }))}
                                    tick={(payload) => {
                                        const item = videos.find((item: { videoUrl: string, value: number }) => item.videoUrl === payload.value) || { videoUrl: '', value: 0 };
                                        const content = websiteContent?.find((content: WebsiteBuilderContent) => content.videoContent?.video === item.videoUrl);
                                        return renderBarChartTick(payload, WebsiteChartTypes.VIDEOS, item.value, content);
                                    }}
                                />
                            </CardSymphony>
                        )}
                    </>
                    :
                    <BlurredGraph
                        className="contentAnalyticsBlurredGraph"
                        graph="topContent"
                        buttonText="Upgrade to Lite"
                        upgradeSource="Website Builder - Content Analytics"
                    />
                }
            </TabSymphony>
        )
    }

    const renderCollectDataTable = () => {
        if (!isCollectDataContentExists || isLoadingWebsiteCollectedData) {
            return null;
        }

        if (!websiteCollectedData[currentContentId]?.length) {
            return (
                <WebsiteBuilderEmptyState
                    className="collectedDataTableEmptyState"
                    title="No data collected yet"
                    subtitle="Collect data from your website to see the data here"
                />
            )
        }

        return (
            <TableElement
                className="collectedDataTable"
                rowData={websiteCollectedData[currentContentId]}
                columnDefs={columnDefsCollectData}
                minWidth={1150}
            />
        )
    }

    const renderTourDataTable = () => {
        if (!isTourContentExists || isLoadingWebsiteTourData) {
            return null;
        }

        if (!websiteTourData?.length) {
            return (
                <WebsiteBuilderEmptyState
                    className="collectedDataTableEmptyState"
                    title="No tour data collected yet"
                    subtitle="Collect tour data from your website to see the data here"
                />
            )
        }

        return (
            <TableElement
                className="tourDataTable"
                rowData={websiteTourData}
                columnDefs={columnDefsTourData}
                minWidth={1150}
            />
        )
    }

    const renderSignupDataTab = () => {
        if (!isCollectDataContentExists || !websiteCollectedData) {
            return null;
        }

        return (
            <TabSymphony
                className="signupDataTab"
                visible={selectedWebsiteAnalyticsTab.id === 'signupData'}
                style={{
                    gap: 16,
                }}
            >
                {renderDownloadCollectDataCsvButton()}

                {renderCollectDataTable()}
            </TabSymphony>
        )
    }

    const renderTourDataTab = () => {
        if (!isTourContentExists || !websiteTourData) {
            return null;
        }

        return (
            <TabSymphony
                className="tourDataTab"
                visible={selectedWebsiteAnalyticsTab.id === 'tourData'}
                style={{
                    gap: 16,
                }}
            >
                {renderDownloadTourDataCsvButton()}

                {renderTourDataTable()}
            </TabSymphony>
        )
    }

    const renderDateRangeSelector = () => {
        return (
            <div className="dateRangeSelectorContainer" style={sty.dateRangeSelectorContainer}>
                <InputSymphony
                    className="dateRangeSelector"
                    isSelectDropdown
                    maxWidth={200}
                    value={dateRange}
                    options={DATE_RANGE_OPTIONS}
                    onOptionSelect={(option) => handleDateRangeChange(option.value)}
                />

                {showCustomDateRange &&
                    <div
                        className="customDateRangeContainer"
                        style={sty.customDateRangeContainer}
                    >
                        <DatePickerSymphony
                            className="startDatePicker"
                            selected={startDate}
                            onChange={handleStartDateChange}
                            label="From:"
                            dateFormat="MM/dd/yyyy"
                            maxDate={endDate || new Date()}
                            maxWidth={200}
                            minDate={null}
                        />
                        <DatePickerSymphony
                            className="endDatePicker"
                            selected={endDate}
                            onChange={handleEndDateChange}
                            label="To:"
                            dateFormat="MM/dd/yyyy"
                            minDate={startDate || null}
                            maxDate={new Date()}
                            maxWidth={200}
                        />
                    </div>
                }
            </div>
        );
    };

    const renderDownloadCollectDataCsvButton = () => {
        return (
            <ButtonSymphony
                className="downloadCsvButton"
                style={{ marginBottom: 16 }}
                onClick={handleDownloadCollectDataCsv}
                iconLeft="gem"
            >
                Download CSV
            </ButtonSymphony>
        )
    }

    const renderDownloadTourDataCsvButton = () => {
        return (
            <ButtonSymphony
                className="downloadCsvButton"
                style={{ marginBottom: 16 }}
                onClick={handleDownloadTourDataCsv}
                iconLeft="gem"
            >
                Download CSV
            </ButtonSymphony>
        )
    }

    return (
        <CardSymphony
            className="analyticsContent"
            title="High-Level Stats"
            subtitle="See how your website is performing"
            size="small"
        >
            {/* <DebugBox
                alwaysShow
                data={{
                    websiteAnalyticsData,
                }}
            /> */}

            {renderDateRangeSelector()}
            {renderTopLevelStats()}
            {renderTopLevelStatsGraph()}
            {renderTopLevelStatsToggle()}

            <TabsSymphony
                className="analyticsTabs"
                tabs={WEBSITE_ANALYTICS_TABS}
                selectedTab={selectedWebsiteAnalyticsTab}
                onChange={(tab) => setSelectedWebsiteAnalyticsTab(tab as TabItem)}
                isBorderBottom
                isLoading={isLoadingWebsiteCollectedData || isLoadingWebsiteTourData}
            />
            {renderContentAnalyticsTab()}
            {renderDemographicsTab()}
            {renderSignupDataTab()}
            {renderTourDataTab()}
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    topLevelStatsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
    },
    statStyle: {
        width: '100%',
    },
    chartContainer: {
        marginTop: 24,
        width: '100%',
    },
    topLevelStatsToggleContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        marginTop: 24,
        flexWrap: 'wrap',
    },
    graphCard: {
        gap: 0,
        border: Sty.border
    },
    graphTab: {
        gap: 16,
    },
    dateRangeSelectorContainer: {
        marginTop: 16,
        marginBottom: 16,
    },
    customDateRangeContainer: {
        display: 'flex',
        gap: 16,
        marginTop: 16,
    }
}

export default WebsiteBuilderAnalytics;